import { Routes } from '@angular/router';
import { adminGuard } from './core/guards/admin.guard';
import { authGuard } from './core/guards/auth.guard';
import { externalLandingGuard } from './core/guards/external-landing.guard';
import { internalOnlyGuard } from './core/guards/internal-only.guard';
import { reserveRequirementSummaryGuard } from './core/guards/reserve-requirement-summary.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { moduleTypeResolver } from './features/financial-submissions/module-type.resolver';
import { sllWorksheetResolver } from './features/sll-calculation/sll-worksheet.resolver';

const setupPlaceholder = () =>
  import('./features/setup-placeholder/setup-placeholder.component').then(
    (m) => m.SetupPlaceholderComponent
  );

export const baseRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [authGuard, externalLandingGuard],
    loadComponent: () =>
      import('./features/landing-page/landing-page.component').then(
        (m) => m.LandingPageComponent
      )
  },
  {
    path: 'lender-overview',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/external-lenders-landing/external-lenders-landing.component').then(
        (m) => m.ExternalLendersLandingComponent
      )
  },
  {
    path: 'lender-overview/external-lenders',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/external-lenders-landing/external-lenders-landing.component').then(
        (m) => m.ExternalLendersLandingComponent
      )
  },
  {
    path: 'lender-overview/external-lenders/:lenderName',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/external-lenders-landing/external-lenders-landing.component').then(
        (m) => m.ExternalLendersLandingComponent
      )
  },
  {
    path: 'lender-overview/:lenderName',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/external-lenders-landing/external-lenders-landing.component').then(
        (m) => m.ExternalLendersLandingComponent
      )
  },
  {
    path: 'setup/lender-setup',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-setup/lender-setup-page.component').then(
        (m) => m.LenderSetupPageComponent
      )
  },
  {
    path: 'setup/lender-setup/list',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-setup/lender-setup-page.component').then(
        (m) => m.LenderSetupPageComponent
      )
  },
  {
    path: 'setup/lender-setup/new',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-overview/lender-overview.component').then(
        (m) => m.LenderOverviewComponent
      )
  },
  {
    path: 'setup/lender-setup/:lenderId',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-overview/lender-overview.component').then(
        (m) => m.LenderOverviewComponent
      )
  },
  {
    path: 'portfolio-setup',
    canActivate: [authGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () =>
      import('./features/portfolio-setup/portfolio-setup-page.component').then(
        (m) => m.PortfolioSetupPageComponent
      )
  },
  {
    path: 'setup/cls-lender-setup',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/cls-lender-list/cls-lender-list.component').then(
        (m) => m.ClsLenderListComponent
      )
  },
  {
    path: 'setup/cls-lender-setup/:lenderName',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/cls-lender-level-setup/cls-lender-level-setup.component').then(
        (m) => m.ClsLenderLevelSetupComponent
      )
  },
  {
    path: 'setup/cls-loan-setup',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/cls-loan-setup/cls-loan-setup-search-page.component').then(
        (m) => m.ClsLoanSetupSearchPageComponent
      ),
    data: { title: 'CLS Loan Level Setup' }
  },
  {
    path: 'setup/cls-loan-setup/edit',
    canActivate: [authGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () =>
      import('./features/cls-loan-setup/cls-loan-setup-editor-page.component').then(
        (m) => m.ClsLoanSetupEditorPageComponent
      ),
    data: { title: 'CLS Loan Level Setup Editor' }
  },
  {
    path: 'setup/agency-credit-rating',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/agency-credit-rating-setup/agency-credit-rating-setup-page.component').then(
        (m) => m.AgencyCreditRatingSetupPageComponent
      )
  },
  {
    path: 'setup/lender-collateral-accounts',
    canActivate: [adminGuard],
    loadComponent: () =>
      import('./features/lender-collateral-account-setup/lender-collateral-account-setup-page.component').then(
        (m) => m.LenderCollateralAccountSetupPageComponent
      ),
    data: { title: 'Lender Collateral Accounts Setup' }
  },
  {
    path: 'setup/cls-rule-derivation',
    canActivate: [authGuard],
    loadComponent: setupPlaceholder,
    data: { title: 'CLS Rule Derivation Utility' }
  },
  {
    path: 'setup/variable-type',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/variable-type-setup/variable-type-list.component').then(
        (m) => m.VariableTypeListComponent
      )
  },
  {
    path: 'setup/sll-rules-setup',
    canActivate: [authGuard, internalOnlyGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () =>
      import('./features/sll-rules-setup/sll-rules-setup-page.component').then(
        (m) => m.SllRulesSetupPageComponent
      ),
    data: { title: 'SLL Rules Setup' }
  },
  {
    path: 'sll/lenders/:lenderId/periods/:financialPeriod',
    canActivate: [authGuard, internalOnlyGuard],
    canDeactivate: [unsavedChangesGuard],
    resolve: { worksheet: sllWorksheetResolver },
    loadComponent: () =>
      import('./features/sll-calculation/sll-calculation-page.component').then(
        (m) => m.SllCalculationPageComponent
      ),
    data: { title: 'SLL Calculation' }
  },
  {
    path: 'setup/lock-unlock-admin',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/lock-unlock-admin/lock-unlock-admin-page.component').then(
        (m) => m.LockUnlockAdminPageComponent
      ),
    data: { title: 'Lender Lock/ Unlock' }
  },
  {
    path: 'setup/configuration',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/configuration-setup/configuration-setup-page.component').then(
        (m) => m.ConfigurationSetupPageComponent
      )
  },
  {
    path: 'setup/ratings-classifications',
    canActivate: [authGuard],
    loadComponent: setupPlaceholder,
    data: { title: 'Ratings Classifications Setup' }
  },
  {
    path: 'setup/agency-rating-scale',
    canActivate: [authGuard],
    loadComponent: setupPlaceholder,
    data: { title: 'Agency Rating Scale Setup' }
  },
  {
    path: 'access-denied',
    loadComponent: () =>
      import('./features/access-denied/access-denied.component').then(
        (m) => m.AccessDeniedComponent
      )
  },
  {
    path: 'reports/daily-collateral-summary',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/reports/collateral-monitoring/collateral-monitoring.component').then(
        (m) => m.CollateralMonitoringComponent
      )
  },
  {
    path: 'custodian/reserve-requirement-summary',
    canActivate: [authGuard, reserveRequirementSummaryGuard],
    loadComponent: () =>
      import('./features/custodian/reserve-requirement-summary/reserve-requirement-summary.component').then(
        (m) => m.ReserveRequirementSummaryComponent
      )
  },
  {
    path: 'variable-types',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/variable-type-setup/variable-type-list.component').then(
        (m) => m.VariableTypeListComponent
      )
  },
  {
    path: 'reference-lists',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/reference-list-setup/reference-list.component').then(
        (m) => m.ReferenceListComponent
      )
  },
  {
    path: 'compliance/cls-calc-review',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/cls-calc-review-bank/cls-calc-review-bank.component').then(
        (m) => m.ClsCalcReviewBankComponent
      )
  },
  {
    path: 'compliance/generate-review-worksheet/:id/loan-level',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/generate-req-review-worksheet/req-review-loan-level.component').then(
        (m) => m.ReqReviewLoanLevelComponent
      )
  },
  {
    path: 'compliance/generate-review-worksheet/:id/view-detailed',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/generate-req-review-worksheet/req-amt-calc-worksheet-detailed.component').then(
        (m) => m.ReqAmtCalcWorksheetDetailedComponent
      )
  },
  {
    path: 'compliance/generate-review-worksheet/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/generate-req-review-worksheet/req-amt-calc-worksheet.component').then(
        (m) => m.ReqAmtCalcWorksheetComponent
      )
  },
  {
    path: 'compliance/generate-review-worksheet',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/generate-req-review-worksheet/req-review-worksheet.component').then(
        (m) => m.ReqReviewWorksheetComponent
      )
  },
  {
    path: 'compliance/lender-compliance-review-status-overview',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-compliance-status-overview/lender-compliance-status-overview.component').then(
        (m) => m.LenderComplianceStatusOverviewComponent
      ),
    data: { title: 'Lender Compliance Review - Status Overview' }
  },
  // ── Reports ───────────────────────────────────────────────────────────────
  {
    path: 'reports/dus-capital-compliance',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/dus-capital-compliance/dus-capital-compliance-page.component').then(
        (m) => m.DusCapitalCompliancePageComponent
      ),
    data: { title: 'DUS Capital Compliance' }
  },
  {
    path: 'compliance',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-compliance-status-overview/lender-compliance-status-overview.component').then(
        (m) => m.LenderComplianceStatusOverviewComponent
      ),
    data: { title: 'Lender Compliance Review - Status Overview' }
  },
  // ── Financial Submissions (Landing + Bank) ───────────────────────────────
  {
    path: 'financial-submissions',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/financial-submissions/financial-submissions-landing.component').then(
        (m) => m.FinancialSubmissionsLandingComponent
      ),
    data: { title: 'Financial Submissions' }
  },
  {
    path: 'financial-submissions/:lenderId/:financialPeriod',
    canActivate: [authGuard],
    resolve: { moduleType: moduleTypeResolver },
    loadComponent: () =>
      import('./features/financial-submissions/financial-submissions-detail-dispatcher.component').then(
        (m) => m.FinancialSubmissionsDetailDispatcherComponent
      ),
    data: { title: 'Lender Quarterly Financials' }
  },
  // ── Financial Submission Non-Bank ────────────────────────────────────────────
  {
    path: 'financial-submission/non-bank',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/financial-submission-non-bank/financial-submission-non-bank-page.component').then(
        (m) => m.FinancialSubmissionNonBankPageComponent
      ),
    data: { title: 'Financial Submission — Non-Bank' }
  },
  {
    path: 'financial-submission/non-bank/:lenderId/:financialPeriod',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/financial-submission-non-bank/financial-submission-non-bank-page.component').then(
        (m) => m.FinancialSubmissionNonBankPageComponent
      ),
    data: { title: 'Financial Submission — Non-Bank' }
  },
  {
    path: 'financial-submissions',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/financial-submissions/financial-submissions-landing.component').then(
        (m) => m.FinancialSubmissionsLandingComponent
      )
  },

  // ── Lender Attestation ───────────────────────────────────────────────────────
  {
    path: 'lender-attestation/:lenderId/:reportingPeriod',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-attestation/attestation-review.component').then(
        (m) => m.AttestationReviewComponent
      ),
    data: { title: 'Lender Quarterly Attestation' }
  },

  // ── Reports ──────────────────────────────────────────────────────────────────
  {
    path: 'reports/lender-concentration-risk',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/reports/lender-concentration-risk/lender-concentration-risk.component').then(
        (m) => m.LenderConcentrationRiskComponent
      ),
    data: { title: 'Lender Concentration Risk' }
  },
  {
    path: 'reports/lender-loss-sharing',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/lender-loss-sharing-report/lender-loss-sharing-report.component').then(
        (m) => m.LenderLossSharingReportComponent
      ),
    data: { title: 'Lender Loss Sharing' }
  },
  {
    path: '**',
    redirectTo: ''
  }
];